import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { EyeBlueIcon } from "../../../assets";
import { TaskConstants } from "../../../constants/workflow/TaskConstants";
import { CursorChangeProps, Origin, ResultType } from "../../../models";
import { GetTaskBoardResponse, searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../service/query";
import { ResultData } from "../../../service/Shared";
import { useTaskPanelWorkflow } from "../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../useAuth";
import { flattenObject } from "../../../utils";
import { getDealListingRoute, getProjectDetailsByUuid } from "../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  dataGridMapFilterCriteria,
} from "../../../widget";
import { IconCellFormatterData } from "../../../widget/data/DataGrid/models";

interface UseYourTasksWidgetReturnData {
  columnsTasks: DataGridColumnDefinition[];
  tasksDataLoad: DataGridCursorDataLoadEventHandler;
  isTasks: boolean;
  hasTasks: (isTasks: boolean) => void;
  isYourTasksPanelOpen: boolean;
  setIsYourTasksPanelOpen: (isOpen: boolean) => void;
  refreshTasks: boolean;
  handleRefresh: () => void;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void;
  showWorkflowTaskPanel: boolean;
  setShowWorkflowTaskPanel: (show: boolean) => void;
  handleCloseWorkflowTaskPanel: () => void;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
}

const columnsTasks: DataGridColumnDefinition[] = [
  {
    key: "taskLink",
    name: "Task",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    filterable: true,
    minWidth: 200,
    sortable: false,
  },
  {
    // "General" for a general task, otherwise the workflow object it belongs to, linked as before.
    key: "objectLink",
    name: "Type",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    filterable: true,
    minWidth: 200,
    sortable: false,
  },
  {
    key: "deadlineDate",
    name: "Deadline",
    dataType: "Date",
    formatter: "dateOnlyWithIndicator",
    alignment: "left",
    minWidth: 200,
  },
  {
    key: "updatedAt",
    name: "Last updated",
    dataType: "Date",
    formatter: "dateOnly",
    alignment: "left",
    minWidth: 200,
  },
  {
    key: "viewButton",
    name: "View",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    minWidth: 160,
  },
];

export const useYourTasksWidget = (): UseYourTasksWidgetReturnData => {
  const navigate = useNavigate();
  const { user, currentUserType } = useAuth();

  const [isTasks, hasTasks] = useState(false);
  const [isYourTasksPanelOpen, setIsYourTasksPanelOpen] = useState(false);
  const [refreshTasks, setRefreshTasks] = useState(false);
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const workflow = useTaskPanelWorkflow(selectedPanelTask?.workflowUuid);

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  const formatData = useCallback(
    (responseData: SearchWorkflowTasksResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.taskLink = {
          text: el.name,
          action: () => {
            setSelectedPanelTask(el);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        result.viewButton = {
          action: () => {
            setSelectedPanelTask(el);
            setShowWorkflowTaskPanel(true);
          },
          icon: <EyeBlueIcon width={20} height={20} strokeWidth={1.5} />,
        } as IconCellFormatterData;

        const { workflowObjectUuid, workflowObjectType } = el;
        if (el.step == null) {
          /*
            A general task has no workflow, so the column states its type instead. Without an action the
            formatter renders plain text rather than a link, which is right — there is nothing to open.
          */
          result.objectLink = {
            text: TaskConstants.TASK_TYPE_GENERAL,
          } as DataGridButtonLinkCellFormatterData;
        } else if (workflowObjectUuid) {
          const objectRoute =
            workflowObjectType === "Deal"
              ? getDealListingRoute(workflowObjectUuid, currentUserType)
              : getProjectDetailsByUuid(workflowObjectUuid, currentUserType, { origin: Origin.DashboardPage });
          result.objectLink = {
            text: el.workflowObjectName,
            action: () => {
              navigate(objectRoute);
            },
          } as DataGridButtonLinkCellFormatterData;
        }

        return result;
      }) || [],
    []
  );

  const tasksDataLoad: DataGridCursorDataLoadEventHandler = async ({ paging, filtering }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 5,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (filterCriteria.taskLink) {
      filterCriteria.name = filterCriteria.taskLink;
      filterCriteria.taskLink = undefined;
      delete filterCriteria.taskLink;
    }

    if (filterCriteria.objectLink) {
      filterCriteria.workflowObjectName = filterCriteria.objectLink;
      delete filterCriteria.objectLink;
    }

    filterCriteria.assignee = {
      uuid: {
        operator: "eq",
        value: user?.userUuid || "",
      },
    };

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    await searchWorkflowTasks({
      paging: { limit: 5, beforeCursor: null, afterCursor: null },
      filter: { results: filterCriteria },
      sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
    }).then((response) => {
      const formatted = formatData(response.data);
      hasTasks(formatted.length > 0);
      data = {
        resultData: formatted,
        paging: {
          startCursor: response.data?.paging?.startCursor || "",
          endCursor: response.data?.paging?.endCursor || "",
          hasNextPage: false,
          hasPreviousPage: false,
          pageSize: paging.pageSize || 5,
          totalCount: response.data?.paging?.total || response.data?.results?.length || 0,
        },
      };
    });

    return data;
  };

  return {
    columnsTasks,
    tasksDataLoad,
    isTasks,
    hasTasks,
    isYourTasksPanelOpen,
    setIsYourTasksPanelOpen,
    refreshTasks,
    handleRefresh,
    selectedPanelTask,
    setSelectedPanelTask,
    showWorkflowTaskPanel,
    setShowWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    workflow,
  };
};
